import clsx from 'clsx';
import React from 'react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import Button from '../../components/Button';
import { useButtonSizeSmaller } from '../../utils/buttonSizes';
import ChevronIcon from '../../assets/icons/chevron-down-icon.svg?react';
import moment from 'moment';
import { CompanySize, IndustryType } from '@EHSNavigator/backend';

interface OnboardOrganizationStepThreeProps {
  otherClasses?: string;
  formHandler: any;
  handleSteps: React.Dispatch<number>;
  isLoading: boolean;
}

const OnboardOrganizationStepThree: React.FC<OnboardOrganizationStepThreeProps> = ({
  otherClasses,
  formHandler,
  handleSteps,
  isLoading,
}) => {
  const onboardOrganizationStepThreeClasses = clsx(otherClasses);
  const dispatch = useDispatch();

  const getIndustryTypeLabel = (value: string) => {
    return Object.entries(IndustryType).find(([, v]) => v === value)?.[1] || value;
  };

  const getIndustrySizeLabel = (value: string) => {
    return Object.entries(CompanySize).find(([, v]) => v === value)?.[1] || value;
  };

  return (
    <div
      className={onboardOrganizationStepThreeClasses}
      data-testid="onboard-organization-step-three"
    >
      <div className="flex max-w-818 items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div>
          <h1 className="h3">Review Organization Details</h1>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
            Review the organization details. By submitting, you agree to the terms
            and confirm the details are accurate.
          </p>
        </div>
      </div>
      <div className="lg:mt-9">
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-form-input mt-15 text-base leading-5 font-normal italic lg:hidden">
          Review the organization details. By submitting, you agree to the terms and
          confirm the details are accurate.
        </p>
        <div className="lg:border-t-neutral-tones-text/10 py-8 lg:border-t">
          <div className="flex max-w-950 flex-col items-start justify-between gap-9 md:flex-row md:gap-12 lg:flex-wrap lg:gap-x-100 lg:gap-y-8">
            <div className="flex w-full flex-col md:w-auto">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
                Organization Details
              </h6>
              <div className="grid grid-cols-2 gap-x-75 gap-y-6">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Company Name
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {formHandler?.values?.companyName}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Industry Type
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {getIndustryTypeLabel(formHandler?.values?.industryType)}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Industry Size
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {getIndustrySizeLabel(formHandler?.values?.industrySize)}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Headquarter Address
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {formHandler?.values?.headquarterAddress}
                  </p>
                </div>
                {formHandler?.values?.organizationContact && (
                  <div className="flex flex-col gap-2.5">
                    <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                      Organization Contact
                    </h6>
                    <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                      {formHandler?.values?.organizationContact}
                    </p>
                  </div>
                )}
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    License Count
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {formHandler?.values?.licenseCount || 'N/A'}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Payment Status
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {formHandler?.values?.isPaid ? 'Payment Completed' : 'Payment Pending'}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Amount
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {formHandler?.values?.amount ? `$${formHandler?.values?.amount}` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <h6 className="font-inter-variable text-brand-byzantine -tracking-button mb-5 text-lg leading-5 font-bold md:mb-25 lg:text-xl lg:leading-6">
                Owner Details
              </h6>
              <div className="grid grid-cols-2 gap-x-75 gap-y-6">
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Full Name
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal">
                    {formHandler?.values?.fullName}
                  </p>
                </div>
                <div className="flex flex-col gap-2.5">
                  <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                    Email
                  </h6>
                  <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                    {formHandler?.values?.email}
                  </p>
                </div>
                {formHandler?.values?.contact && (
                  <div className="flex flex-col gap-2.5">
                    <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                      Contact
                    </h6>
                    <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                      {formHandler?.values?.contact}
                    </p>
                  </div>
                )}
                {formHandler?.values?.dob && (
                  <div className="flex flex-col gap-2.5">
                    <h6 className="text-neutral-tones-text -tracking-form-input lg:-tracking-button font-inter-variable text-base leading-6 font-bold lg:text-lg lg:leading-6">
                      Date Of Birth
                    </h6>
                    <p className="font-inter-variable text-neutral-tones-text -tracking-form-input text-base leading-5 font-normal break-words">
                      {moment(formHandler?.values?.dob).format('MMM DD YYYY')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
          <Button
            onClick={() => handleSteps(2)}
            size={useButtonSizeSmaller()}
            type="button"
            variant="secondary"
            otherClasses="w-full sm:max-w-211"
          >
            <ChevronIcon className="h-5 w-5 rotate-90 [&>path]:fill-current" />{' '}
            Go Back
          </Button>
          <Button
            type="submit"
            onClick={() => formHandler?.handleSubmit()}
            size={useButtonSizeSmaller()}
            otherClasses="w-full sm:w-fit"
            disable={isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Please wait' : 'Create Organization'}
            <ChevronIcon className="h-5 w-5 -rotate-90 [&>path]:fill-current" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardOrganizationStepThree;
